import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Blocks, Fingerprint, UserPlus } from "lucide-react";
import type { JSX } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { InviteMemberModal } from "@/app/invitations/components/modals/invite-member-modal";
import { OnboardingPageTitle } from "@/app/onboarding-first-win/components/onboarding-page-title";
import { useLeaveOnboarding } from "@/app/onboarding-first-win/hooks/use-leave-onboarding";
import { useOnboardingTelemetry } from "@/app/onboarding-first-win/hooks/use-onboarding-telemetry";
import { NextStepCard } from "./next-step-card";

const NEXT_STEP_EVENT = "Onboarding next step selected";

export function NextStepsStep(): JSX.Element {
  const leaveOnboarding = useLeaveOnboarding();
  const { captureOnboardingEvent } = useOnboardingTelemetry();

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto px-4 pt-8 pb-8 md:px-8 md:pt-12 md:pb-10">
      <div className="mx-auto w-full max-w-5xl space-y-5 md:space-y-8">
        <div>
          <OnboardingPageTitle>
            Your agent just took its first real action through Arcade
          </OnboardingPageTitle>
          <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
            Your gateway is live and serving tools. Here are a few good places
            to go from here:
          </p>
        </div>

        <ul className="max-w-2xl space-y-2 md:space-y-3">
          <li>
            <NextStepCard
              description="Browse 700+ tools built for the SaaS apps your team already uses."
              icon={Blocks}
              label="Tool Catalog"
              onClick={() =>
                leaveOnboarding({
                  to: "/orgs/$organizationId/projects/$projectId/tools",
                  event: NEXT_STEP_EVENT,
                  properties: {
                    current_step: "done",
                    next_step: "tool_catalog",
                  },
                })
              }
            />
          </li>
          <CanProject permission={PROJECT.invitations.create}>
            <li>
              <InviteMemberModal
                trigger={
                  <NextStepCard
                    description="Bring your team in so they can put this gateway to work too."
                    icon={UserPlus}
                    label="Invite teammates"
                    onClick={() =>
                      captureOnboardingEvent(NEXT_STEP_EVENT, {
                        current_step: "done",
                        next_step: "invite_teammates",
                      })
                    }
                  />
                }
              />
            </li>
          </CanProject>
          <li>
            <NextStepCard
              description="See how Arcade fits the identity systems your org already runs on."
              icon={Fingerprint}
              label="User Sources"
              onClick={() =>
                leaveOnboarding({
                  to: "/orgs/$organizationId/projects/$projectId/user-sources",
                  event: NEXT_STEP_EVENT,
                  properties: {
                    current_step: "done",
                    next_step: "user_sources",
                  },
                })
              }
            />
          </li>
        </ul>

        <Button
          onClick={() =>
            leaveOnboarding({
              to: "/orgs/$organizationId/projects/$projectId/playground/chat",
              event: NEXT_STEP_EVENT,
              properties: {
                current_step: "done",
              },
            })
          }
          size="lg"
          type="button"
          variant="outline"
        >
          Take me to the dashboard
        </Button>
      </div>
    </div>
  );
}
